import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../context/UserContext';

interface Props {
  data: User | undefined;
  page?: string; // 선택적 page props
}

const MembershipBadge: React.FC<Props> = ({ data, page }) => {
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(true);
  const [regionStatus, setRegionStatus] = useState<string>('');

  const membershipLabelMap: Record<string, string> = {
    P: '프리미엄',
    S: '스탠다드',
    // T: 'Promotion',
    T: '무료체험중',
  };

  const label = data?.isCoupon
    ? '쿠폰'
    : data?.plan_grade
      ? (membershipLabelMap[data.plan_grade] ?? '무료')
      : '무료';

  useEffect(() => {
    const fetchRegionText = async () => {
      if (!data) return;
      const planRegion = data.plan_region;
      if (!planRegion) return [];
      const regionCode = planRegion.split(',');

      const regionMap: Record<string, string> = {
        A: '전국',
        B: '수도권(서울,경기,인천)',
        C: '서울',
        D: '경기',
        E: '인천',
        F: '지방 통합(수도권 제외 전지역)',
        G: '충청권',
        H: '강원권',
        I: '경상권',
        J: '호남권',
        K: '제주도',
      };

      const regions = regionCode.map((code: string) => regionMap[code]);
      const parts = regions[0].split('/');
      const region = parts[1] ? parts[1] : parts[0];
      if (regions.length > 1) {
        setRegionStatus(`${region} 외${regions.length - 1}`);
      } else {
        setRegionStatus(regions[0]);
      }
    };
    if (data?.plan_grade) {
      fetchRegionText();
    }
  }, [label]);

  const getStatusText = () => {
    switch (label) {
      case 'Free':
        return '무료회원';
      case 'Promotion':
        return '혜택적용';
      case '무료체험중':
        return '';
      default:
        return '이용중';
    }
  };

  const getRemainingTimeText = () => {
    if (!data?.plan_end) return '';

    const end = new Date(`${data.plan_end}T23:59:59+09:00`);

    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
    const koreaTimeDiff = 9 * 60 * 60 * 1000;
    const koreaTime = new Date(utc + koreaTimeDiff);

    const diffMs = end.getTime() - koreaTime.getTime();

    if (diffMs <= 0) return '남은 시간이 없습니다.';

    const diffMin = Math.floor(diffMs / 1000 / 60);
    const days = Math.floor(diffMin / 60 / 24);
    const hours = Math.floor((diffMin % (60 * 24)) / 60);
    const minutes = diffMin % 60;

    return `${days}일 ${hours}시간 ${minutes}분 남았습니다.`;
  };

  if (!data) {
    return null;
  }

  return (
    <MemberShip>
      <BadgeStatusGroup>
        <MemberShipBadge label={label}>
          <span style={{ fontFamily: 'Paperlogy', fontWeight: '700' }}>
            {label}
          </span>
          {showTooltip && label === '무료체험중' && (
            <TooltipBox>
              <TooltipText>
                무료체험 기한이 {getRemainingTimeText()}
              </TooltipText>
              <TooltipButtons>
                <TooltipButtonGray onClick={() => setShowTooltip(false)}>
                  닫기
                </TooltipButtonGray>
                <TooltipButtonBlue onClick={() => navigate('/payment/plan')}>
                  요금제 알아보기
                </TooltipButtonBlue>
              </TooltipButtons>
            </TooltipBox>
          )}
        </MemberShipBadge>
        <MemberShipStatus label={label}>
          {!data.plan_grade
            ? '무료회원'
            : label === '쿠폰'
              ? membershipLabelMap[data.plan_grade]
              : regionStatus}
        </MemberShipStatus>
      </BadgeStatusGroup>
      {page === 'quickmenu' &&
        (label === '무료' ? (
          <MemberShipLink onClick={() => navigate('/payment/plan')}>
            <span>요금제 보기</span>
            <img
              src="/assets/images/arrow/Arrow-right-white.svg"
              alt=""
              style={{ width: '16px', height: '16px' }}
            />
          </MemberShipLink>
        ) : (
          <MemberShipDate>
            {data?.plan_start?.slice(2).replace(/-/g, '.')}~
            {data?.plan_end?.slice(2).replace(/-/g, '.')}
          </MemberShipDate>
        ))}
    </MemberShip>
  );
};

export default MembershipBadge;

const MemberShip = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BadgeStatusGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 14px;
`;

const MemberShipBadge = styled.div<{ label: string | null | undefined }>`
  height: 24px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  position: relative;

  ${({ label }) => {
    switch (label) {
      case '프리미엄':
        return css`
          background:
            linear-gradient(to right, #008aff, #6900f3),
            linear-gradient(to right, #3f9bff, #7c29fb);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          border: 2px solid transparent;
          border-radius: 4px;
        `;
      case '스탠다드':
        return css`
          background:
            linear-gradient(to right, #00a2e7, #aad545),
            linear-gradient(to right, #3cb5de, #aad545);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          border: 2px solid transparent;
          border-radius: 4px;
        `;
      case '무료체험중':
        return css`
          background:
            linear-gradient(to right, #fab914, #ff8000),
            linear-gradient(to right, #ffde55, #ff9a57);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          border: 2px solid transparent;
          border-radius: 4px;
        `;
      case '쿠폰':
        return css`
          background: linear-gradient(105.88deg, #00dcec 0%, #ff2ad8 100%);
          border: 2px solid;

          border-image-source: linear-gradient(
            94.45deg,
            #23ddeb 0%,
            #ff72e5 100%
          );

          background-origin: border-box;
          background-clip: padding-box, border-box;
          border: 2px solid transparent;
          border-radius: 4px;
        `;
      case undefined:
      case null:
      default:
        return css`
          background:
            linear-gradient(to right, #c4c6cc, #85898e),
            linear-gradient(to right, #e0e1e6, #bdc1c7);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          border: 2px solid transparent;
          border-radius: 4px;
        `;
    }
  }}
`;

const MemberShipStatus = styled.div<{ label: string | null | undefined }>`
  font-family: 'Paperlogy';
  font-size: 14px;
  font-weight: 700;

  ${({ label }) => {
    switch (label) {
      case '프리미엄':
        return css`
          color: #6507f4;
        `;
      case '스탠다드':
        return css`
          color: #94bf08;
        `;
      case '무료체험중':
        return css`
          color: #ff8000;
        `;
      case '쿠폰':
        return css`
          color: #ff2ad8;
        `;
      case undefined:
      case null:
      default:
        return css`
          color: #898d92;
        `;
    }
  }}
`;

const MemberShipDate = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-family: Spoqa Han Sans Neo;
  color: #222222;
`;

const MemberShipLink = styled.div`
  height: 24px;
  border-radius: 4px;
  background-color: #008aff;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: row;
  padding: 2px 1px 2px 6px;
  cursor: pointer;
`;

const TooltipBox = styled.div`
  position: absolute;
  top: 32px;
  left: 0;
  padding: 14px;
  background-color: #ffffff;
  border: 1px solid #008aff;
  border-radius: 8px;
  box-shadow: 0px 2px 12px 0px #00000033;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: nowrap;
  width: fit-content;

  &::after {
    content: '';
    position: absolute;
    top: -7px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 8px solid #ffffff;
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 8px solid #008aff;
    z-index: 0;
  }
`;

const TooltipText = styled.div`
  font-size: 14px;
  color: #008aff;
  font-weight: 500;
`;

const TooltipButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
`;

const TooltipButtonGray = styled.button`
  padding: 7px 24px;
  background-color: #fafafb;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;

  color: #616161;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
`;

const TooltipButtonBlue = styled.button`
  flex: 1;
  padding: 7px;
  background-color: #008aff;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  outline: none;

  color: white;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
`;
